<!-- Deleting overlay: blocks interaction until the delete-doctor call completes -->
<div *ngIf="isDeleting"
    class="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9999] flex items-center justify-center">
    <div class="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
        <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
        <p class="font-black text-slate-800 uppercase tracking-tight text-sm">{{ 'doctors_delete_loading' | translate }}</p>
    </div>
</div>

<div class="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center w-full sm:w-auto mb-6">
    <!-- Search Bar -->
    <p-iconField iconPosition="left" class="w-full sm:w-auto flex-1">
        <p-inputIcon styleClass="pi pi-search text-slate-400" />
        <input type="text" pInputText [(ngModel)]="searchTerm" (ngModelChange)="onSearch($event)"
            placeholder="{{ 'doctors_search_placeholder' | translate }}"
            class="w-full !bg-white !border-slate-200 !rounded-xl !text-sm focus:!ring-2 focus:!ring-primary/10 focus:!border-primary !shadow-sm transition-all" />
    </p-iconField>

    <!-- Speciality filter (replaces the comma-joined speciality column) -->
    <p-multiSelect class="w-full sm:w-72 speciality-filter"
        [options]="specialityOptions.grouped ? specialityOptions.groups : specialties"
        [group]="specialityOptions.grouped" optionGroupLabel="label" optionGroupChildren="items"
        [ngModel]="selectedSpecialtyIds" (ngModelChange)="onSpecialtyFilterChange($event)"
        optionLabel="name" optionValue="id" [filter]="true" filterBy="name"
        display="chip" [maxSelectedLabels]="200" [showClear]="true" appendTo="body"
        [placeholder]="'doctors_filter_specialty_placeholder' | translate"
        styleClass="w-full !rounded-xl !border-slate-200 !bg-white !text-sm !shadow-sm">
        <ng-template #group let-group>
            <div class="flex items-center justify-between gap-2 w-full pr-1">
                <span class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <i class="pi text-[10px]" [ngClass]="group.kind === 'selected' ? 'pi-check-circle text-emerald-500' : group.id ? 'pi-folder text-primary' : 'pi-tag text-slate-400'"></i>
                    {{ group.label }}
                    <span class="font-semibold text-slate-400 normal-case tracking-normal">({{ group.items.length }})</span>
                </span>
                <button type="button" (click)="toggleSpecialityGroup(group); $event.stopPropagation()"
                    class="text-[10px] font-bold text-primary hover:underline shrink-0">
                    {{ (isSpecialityGroupSelected(group) ? 'speciality_group_unselect_all' : 'speciality_group_select_all') | translate }}
                </button>
            </div>
        </ng-template>
    </p-multiSelect>

    <!-- New Doctor Button -->
    <app-button *hasPermission="'Profissionais:create'" (onClick)="onNewDoctor()"
        label="{{ 'doctors_new_button' | translate }}" icon="pi pi-plus" variant="primary">
    </app-button>
</div>

<!-- Table Container -->
<div class="mt-2" *ngIf="doctorsData$ | async as result">
    <app-prime-table [columns]="columns" [data]="result.data" [loading]="(loading$ | async) || false"
        [totalRecords]="result.count" [rows]="pageSize" [templates]="templates" [lazy]="true"
        (onPage)="onPageChange(($event.first / $event.rows) + 1)">
    </app-prime-table>
</div>

<!-- Column Templates -->
<ng-template #doctorNameTemplate let-row>
    <div class="flex items-center gap-3 cursor-pointer group" [routerLink]="['/professionals', row.doctor_id]">
        <div class="relative w-8 h-8 flex-shrink-0">
            <img [src]="row.photo || 'assets/images/user-profile.png'"
                (error)="row.photo = 'assets/images/user-profile.png'"
                class="w-8 h-8 rounded-full border border-slate-100 object-cover bg-slate-50 shadow-sm"
                [alt]="'alt_doctor_photo' | translate">
        </div>
        <span class="font-medium text-slate-700 group-hover:text-primary transition-colors">{{ row.doctor_name }}</span>
    </div>
</ng-template>

<ng-template #statusTemplate let-row>
    <span
        [class]="'px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-2 ' + (row.status?.toLowerCase() === 'inativo' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600')">
        <div
            [class]="'w-1.5 h-1.5 rounded-full ' + (row.status?.toLowerCase() === 'inativo' ? 'bg-amber-600' : 'bg-emerald-600')">
        </div>
        {{ (row.status?.toLowerCase() === 'inativo' ? 'status_inactive' : 'status_active') | translate }}
    </span>
</ng-template>

<ng-template #actionsTemplate let-row>
    <app-dropdown>
        <ng-template #trigger>
            <button class="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                [title]="'ACTIONS' | translate">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path
                        d="M12 8a2 2 0 100-4 2 2 0 000 4zM12 14a2 2 0 100-4 2 2 0 000 4zM12 20a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
            </button>
        </ng-template>
        <ng-template #content>
            <div class="flex flex-col">
                <button (click)="onView(row)"
                    class="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors uppercase tracking-widest group/item">
                    <svg class="w-4 h-4 text-primary group-hover/item:scale-110 transition-transform" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {{ 'VIEW' | translate }}
                </button>
                <button *hasPermission="'Profissionais:edit'" (click)="onEdit(row)"
                    class="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors uppercase tracking-widest group/edit">
                    <svg class="w-4 h-4 text-primary group-hover/edit:scale-110 transition-transform" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {{ 'EDIT' | translate }}
                </button>
                <button *hasPermission="'Profissionais:edit'" (click)="toggleStatus(row)"
                    class="w-full text-left px-4 py-3 text-xs font-black text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors uppercase tracking-widest group/toggle">
                    <i
                        [class]="'pi ' + (row.status?.toLowerCase() === 'inativo' ? 'pi-check-circle text-emerald-500' : 'pi-ban text-red-500') + ' w-4 h-4 text-center'"></i>
                    {{ (row.status?.toLowerCase() === 'inativo' ? 'action_activate' : 'action_deactivate') | translate
                    }}
                </button>
                <button *hasPermission="'Profissionais:delete'" (click)="deleteDoctor(row)"
                    class="w-full text-left px-4 py-3 text-xs font-black text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors uppercase tracking-widest group/delete">
                    <i class="pi pi-trash w-4 h-4 text-center group-hover/delete:scale-110 transition-transform"></i>
                    {{ 'DELETE' | translate }}
                </button>
            </div>
        </ng-template>
    </app-dropdown>
</ng-template>

<p-confirmDialog></p-confirmDialog>